import { memo, useCallback } from "react";
import { useEventListener } from "usehooks-ts";
import { setShowNewProjectModal } from "src/js/stores/navigation/actions";
import { router } from "src/js/router/react-router";
import ROUTES from "src/js/router/routes";
import { DropdownMenu, DropdownMenuSize } from "react-migration/components/DropdownMenu";
import { LineLoading } from "react-migration/domains/dashboard/documents/components/LineLoading";
import { Button } from "react-migration/components/Button";
import { Icon } from "react-migration/components/Icon";
import { ProjectHatch } from "../ProjectHatch";
import { useSelectedProjectQuery } from "./useSelectedProjectQuery";
import { useDropdownOpenState } from "./useDropdownOpenState";
import { ProjectDropdownList } from "./ProjectDropdownList";
import { setSelectedProject } from "react-migration/domains/projects/util";
import { ProjectEventName } from "react-migration/lib/util/logEventV2";

export const ProjectDropdown = () => {
  const { isOpen, setIsOpen } = useDropdownOpenState();
  const { selectedProject, error, loading } = useSelectedProjectQuery({ forceUpdateLastOpenedDate: true });

  useEventListener("keydown", (e: KeyboardEvent) => {
    if (e.key === "Escape" && isOpen) {
      setIsOpen(false);
    }
  });

  const onCreateNewProject = useCallback(() => {
    setIsOpen(false);
    setShowNewProjectModal(true);
  }, [setIsOpen]);

  const onSelectProject = useCallback(
    (projectId: string) => {
      setSelectedProject(projectId, ProjectEventName.PROJECT_DROPDOWN_PROJECT_SELECTED);
      setIsOpen(false);
    },
    [setIsOpen]
  );

  const onViewAllProjects = useCallback(() => {
    setIsOpen(false);
    router.push({ name: ROUTES.PROJECTS });
  }, [setIsOpen]);

  if (error) {
    return null;
  }

  return (
    <DropdownMenu.Provider
      size={DropdownMenuSize.AUTO}
      offset={0}
      isOpen={isOpen}
      onMenuClosed={() => setIsOpen(false)}
    >
      <DropdownMenu.Trigger
        data-testid="project-dropdown-trigger"
        align={DropdownMenu.TriggerAlignment["BOTTOM_LEFT"]}
        className="p-0! h-full"
        onClick={() => setIsOpen(!isOpen)}
        disabled={loading}
      >
        <div className="relative group h-full w-60 flex items-center overflow-hidden cursor-pointer" role="button">
          <div className="h-full grow flex items-center gap-x-2 pl-4 overflow-hidden border-b border-b-border-action bg-white group-hover:bg-background-button-primary-disabled">
            {loading && !selectedProject ? (
              <>
                <ProjectHatch size="sm" loading />
                <LineLoading />
              </>
            ) : (
              selectedProject && (
                <>
                  <ProjectHatch
                    size="sm"
                    developmentType={selectedProject.developmentType}
                    image={selectedProject.image}
                    imageSrc={selectedProject?.imageUrl ?? undefined}
                  />
                  <span className="grow truncate select-none text-text-base-primary text-sm font-medium">
                    {selectedProject.title}
                  </span>
                </>
              )
            )}
            {isOpen ? (
              <Icon size="md" cursor="pointer" extraClasses="ml-auto" icon="icon-lt-arrow-up-s-line" />
            ) : (
              <Icon size="md" cursor="pointer" extraClasses="ml-auto" icon="icon-lt-arrow-down-s-line" />
            )}
          </div>
          <div className="w-[18px] grow-0 shrink-0">
            <RightTriangle />
          </div>
        </div>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="rounded-t-none! rounded-b-lg z-99999">
        {isOpen && (
          <ProjectDropdownContent
            onCreateNewProject={onCreateNewProject}
            onSelectProject={onSelectProject}
            onViewAll={onViewAllProjects}
          />
        )}
      </DropdownMenu.Content>
    </DropdownMenu.Provider>
  );
};

interface ProjectDropdownContentProps {
  onCreateNewProject(): void;
  onSelectProject(projectId: string): void;
  onViewAll(): void;
}

const ProjectDropdownContent = memo(
  ({ onCreateNewProject, onSelectProject, onViewAll }: ProjectDropdownContentProps) => {
    const isProjectsPage = window.location.pathname.includes("/projects");

    return (
      <div className="p-4 flex flex-col gap-y-3 w-[476px] select-none">
        <div className="flex items-center justify-between">
          <h1 className="text-base font-semibold text-text-base-primary">Recent projects</h1>
          {!isProjectsPage && (
            <Button variant="ghost" size="sm" onClick={onViewAll}>
              View all
              <Icon size="sm" cursor="pointer" icon="icon-lt-arrow-right-line" />
            </Button>
          )}
        </div>
        <ProjectDropdownList onSelectProject={onSelectProject} />
        <div className="border-t-2 border-border-base-divider" />
        <Button variant="ghost" size="md" className="p-1! w-full justify-start" onClick={onCreateNewProject}>
          <Icon
            size="lg"
            cursor="pointer"
            icon="icon-lt-add-line"
            extraClasses="bg-neutral-50 rounded-sm text-text-base-secondary w-10 h-10 flex items-center justify-center"
          />
          New project
        </Button>
      </div>
    );
  }
);

ProjectDropdownContent.displayName = "ProjectDropdownContent";

const RightTriangle = () => (
  <div className="inline-block w-0 h-0 border-solid border-t-26 border-r-0 border-l-18 border-b-26 border-l-white group-hover:border-l-background-button-primary-disabled border-r-transparent border-t-transparent border-b-transparent" />
);
